// Smart Sash Admin Panel - Hood Usage Report Filters
// Cascading multi-select filters + date range

import React, { useRef, useState, useEffect } from 'react';
import { GroupDTO, HoodUsageFilters as FiltersType, InstitutionDTO, LabDTO, UserDTO } from '@/models';

interface MultiSelectProps {
  label: string;
  options: { id: string; name: string }[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  options,
  selectedIds,
  onChange,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((s) => s !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const displayText =
    selectedIds.length === 0
      ? label
      : `${label} (${selectedIds.length})`;

  return (
    <div className="dropdown" ref={ref}>
      <button
        className="btn btn-outline-secondary btn-sm dropdown-toggle w-100 text-start"
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        {displayText}
      </button>
      {isOpen && (
        <div
          className="dropdown-menu show p-2"
          style={{ maxHeight: '250px', overflowY: 'auto', minWidth: '220px' }}
        >
          {options.length === 0 ? (
            <span className="dropdown-item-text text-muted small">No options available</span>
          ) : (
            options.map((opt) => (
              <label
                key={opt.id}
                className="dropdown-item d-flex align-items-center py-1 px-2"
                style={{ cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  className="form-check-input me-2 mt-0"
                  checked={selectedIds.includes(opt.id)}
                  onChange={() => toggle(opt.id)}
                />
                <span className="small">{opt.name}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
};

interface HoodUsageFiltersProps {
  filters: FiltersType;
  availableInstitutions: InstitutionDTO[];
  availableGroups: GroupDTO[];
  availableLabs: LabDTO[];
  availableUsers: UserDTO[];
  onInstitutionChange: (ids: string[]) => void;
  onGroupChange: (ids: string[]) => void;
  onLabChange: (ids: string[]) => void;
  onUserChange: (ids: string[]) => void;
  onDateRangeChange: (from: string | null, to: string | null) => void;
  onClear: () => void;
}

export const HoodUsageFilters: React.FC<HoodUsageFiltersProps> = ({
  filters,
  availableInstitutions,
  availableGroups,
  availableLabs,
  availableUsers,
  onInstitutionChange,
  onGroupChange,
  onLabChange,
  onUserChange,
  onDateRangeChange,
  onClear,
}) => {
  const institutionOptions = availableInstitutions.map((i) => ({
    id: i.id,
    name: i.name || 'Unnamed Institution',
  }));

  const groupOptions = availableGroups.map((g) => ({
    id: g.id,
    name: g.name || 'Unnamed Group',
  }));

  const labOptions = availableLabs.map((l) => ({
    id: l.id,
    name: l.name || 'Unnamed Lab',
  }));

  const userOptions = availableUsers.map((u) => ({
    id: u.id,
    name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Unknown User',
  }));

  const hasActiveFilters =
    filters.institutionIds.length > 0 ||
    filters.groupIds.length > 0 ||
    filters.labIds.length > 0 ||
    filters.userIds.length > 0 ||
    filters.dateFrom !== null ||
    filters.dateTo !== null;

  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-body py-3">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-6 col-lg">
            <label className="form-label small text-muted mb-1">Institution</label>
            <MultiSelect
              label="All Institutions"
              options={institutionOptions}
              selectedIds={filters.institutionIds}
              onChange={onInstitutionChange}
            />
          </div>
          <div className="col-12 col-md-6 col-lg">
            <label className="form-label small text-muted mb-1">Group</label>
            <MultiSelect
              label="All Groups"
              options={groupOptions}
              selectedIds={filters.groupIds}
              onChange={onGroupChange}
            />
          </div>
          <div className="col-12 col-md-6 col-lg">
            <label className="form-label small text-muted mb-1">Lab</label>
            <MultiSelect
              label="All Labs"
              options={labOptions}
              selectedIds={filters.labIds}
              onChange={onLabChange}
            />
          </div>
          <div className="col-12 col-md-6 col-lg">
            <label className="form-label small text-muted mb-1">User</label>
            <MultiSelect
              label="All Users"
              options={userOptions}
              selectedIds={filters.userIds}
              onChange={onUserChange}
            />
          </div>
          <div className="col-12 col-md-6 col-lg">
            <label className="form-label small text-muted mb-1">Date From</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={filters.dateFrom || ''}
              onChange={(e) => onDateRangeChange(e.target.value || null, filters.dateTo)}
            />
          </div>
          <div className="col-12 col-md-6 col-lg">
            <label className="form-label small text-muted mb-1">Date To</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={filters.dateTo || ''}
              onChange={(e) => onDateRangeChange(filters.dateFrom, e.target.value || null)}
            />
          </div>
          <div className="col-12 col-md-auto">
            <button
              className="btn btn-outline-secondary btn-sm w-100"
              onClick={onClear}
              disabled={!hasActiveFilters}
            >
              <i className="fas fa-times me-1"></i>
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
